import * as React from "react";

import { loadHighlighter } from "../lib/highlighter";

type Highlighter = NonNullable<Awaited<ReturnType<typeof loadHighlighter>>>;
type GrammarState = Parameters<Highlighter["highlight"]>[1];

/**
 * Highlights `value` as `language` and returns one React node per line —
 * render each inside a `CodeBlockLine`. Until the language has loaded (or for
 * an unknown language) the lines come back as plain strings.
 *
 * Streaming-safe: while `value` grows (an LLM writing code), only the newly
 * completed lines are tokenized, resuming from the last line's grammar state;
 * the in-progress tail line is re-tokenized on every render.
 */
export function useHighlightedCode(value: string, language: string): React.ReactNode[] {
  // A trailing newline would render as an extra empty line.
  const code = value.endsWith("\n") ? value.slice(0, -1) : value;
  const [loaded, setLoaded] = React.useState<{
    language: string;
    highlighter: Highlighter | null;
  } | null>(null);
  const highlighter = loaded?.language === language ? loaded.highlighter : null;

  React.useEffect(() => {
    let cancelled = false;

    loadHighlighter(language)
      .then((next) => {
        if (!cancelled) {
          setLoaded({ language, highlighter: next });
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [language]);

  // The complete lines, highlighted. While the code streams, only the new
  // lines are tokenized, starting from the grammar state of the last one.
  const [done, setDone] = React.useState<{
    highlighter: Highlighter | null;
    text: string;
    lines: React.ReactNode[];
    state?: GrammarState;
  }>({ highlighter: null, text: "", lines: [] });

  let next = done;

  if (next.highlighter !== highlighter || !code.startsWith(next.text)) {
    next = { highlighter, text: "", lines: [] };
  }

  const end = code.lastIndexOf("\n") + 1;

  if (highlighter && end > next.text.length) {
    const result = highlighter.highlight(code.slice(next.text.length, end - 1), next.state);

    next = {
      highlighter,
      text: code.slice(0, end),
      lines: [...next.lines, ...result.lines],
      state: result.state,
    };
  }

  if (next !== done) {
    // Derived-state update during render (React bails out and re-renders
    // before committing), so streamed lines are never tokenized twice.
    setDone(next);
  }

  // The last line is still being written, so it is tokenized on every render.
  const tail = code.slice(end);

  return React.useMemo(() => {
    if (!highlighter) {
      return code.split("\n");
    }

    return [...next.lines, highlighter.highlight(tail, next.state).lines[0]];
  }, [code, highlighter, next, tail]);
}
